<script setup lang="ts">
useHead({
  title: "Zenapptic AI - Privacy Policy",
});

const appMounted = useState("appMounted");
const { sendScreenView } = useAnalytics();
if (appMounted.value) sendAnalytics();
watch(appMounted, () => sendAnalytics);

function sendAnalytics() {
  sendScreenView({ type: "PageView", target: "PrivacyPolicy" }, true);
}
</script>

<template>
  <div>
    <div class="privacy-page">
      <h1>Zenapptic AI Privacy Policy</h1>

      <div class="body">
        <section>
          <p><em>Last updated: August 17, 2021</em></p>

          <p>
            Zenapptic AI Inc. (“Zenapptic”) is committed to protecting your privacy and complying
            with applicable data protection laws. This Privacy Policy outlines how Zenapptic
            collects, uses, discloses, and safeguards information about you.
          </p>
        </section>

        <section>
          <h2>Zenapptic Services</h2>

          <p>
            Zenapptic offers a suite of applications accessible online, via mobile applications, on
            digital displays and other devices that let users create, manage and deliver interactive
            visual experiences (referred to as “Services”).
          </p>

          <p>
            When using our Services, you may customize and connect your Zenapptic account to
            third-party services (“Third-Party Services”). The collection of your information by
            these third parties is governed by the Third-Party Services’ privacy policies and terms.
            You may also connect to social networks, including but not limited to Facebook,
            Instagram, and Twitter (collectively, the “Social Networks”) which are governed by their
            respective privacy policies and terms. When using Social Networks, you are required to
            comply with their privacy policies and terms. We recommend you carefully review their
            privacy policies and terms.
          </p>

          <p>
            Your use of Zenapptic Services, including any dispute concerning privacy, is subject to
            this Privacy Policy and the Terms of Service. BY USING ZENAPPTIC SERVICES, YOU ARE
            ACCEPTING THE PRACTICES SET OUT IN THIS PRIVACY POLICY AND OUR TERMS OF SERVICE. We
            reserve the right to modify this Privacy Policy at any time by posting the changes on
            this page. Your continued use of any portion of any of the Zenapptic Services following
            posting of the updated Privacy Policy will constitute your acceptance of the changes.
          </p>

          <p>
            Our Services are not intended for use by children and should only be accessed by
            individuals who are at least 18 years old and use the Services for business purposes.
          </p>
        </section>

        <section>
          <h2>What information do we collect?</h2>
          <p>
            We collect information about you as reasonably necessary for the following activities:
          </p>

          <ul>
            <li>
              <b>Browsing:</b> We collect personal information from you when you register for a
              Zenapptic Service and create an account. However, as a visitor to Zenapptic websites,
              you may choose to engage in open e-mail correspondence with us to inquire about our
              Services, in which case we will collect your e-mail address and any personal
              information contained within our e-mail correspondence.
            </li>

            <li>
              <b>Account registration:</b> We will collect your contact information including your
              name, email address, company name, and address.
            </li>

            <li>
              <b>Billing &amp; payment Information:</b> We may collect payment method details, such
              as credit card number or wire transfer information, the Services you have acquired
              from us including the type of plan, number of users, subscription term, and
              transaction information related to the Services.
            </li>

            <li>
              <b>Content:</b> We may collect your social profile information for Social Networks you
              choose to connect to the Services, your messages, posts, comments, images,
              advertising, and other material, and information that is collected from the Social
              Networks that you choose to connect to and which is displayed on our Services.
            </li>

            <li>
              <b>Logs:</b> Log data, which may include your IP address, the address of the web page
              you visited before using the Services, your browser type and settings, your device
              information, the date and time when you used the Services, information about your
              browser configuration, language preferences, unique identifiers, and cookies.
            </li>

            <li>
              <b>Usage data and analytics:</b> This may include the frequency of login, and the
              different types of activity undertaken by users.
            </li>

            <li>
              <b>Location information:</b> In some cases, we collect and store information about
              where you are located, such as by converting your IP address into a rough geolocation.
              We may use location information to improve and personalize our Services for you.
            </li>

            <li>
              <b>Customer support:</b> May include questions, issues, and general feedback that you
              choose to provide.
            </li>

            <li>
              <b>Marketing information:</b> If you choose to participate in surveys, promotions,
              events, or request information from us about our Services, we may collect information
              about you such as your name, email address, telephone number, company name, and
              general information about your company that you choose to provide, such as industry.
              In addition, we may collect information on email open and click rates, including
              whether individuals clicked on links, and which web pages are visited after opening
              the email.
            </li>

            <li>
              <b>Cookies:</b> A “cookie” is a small data file that can be placed on your hard drive
              when you visit certain websites. To better understand how to improve the content of
              Zenapptic websites, we may set a cookie on your computer. Cookies do not identify you
              personally; they merely recognize your browser. By using cookies, we can optimize the
              experience on Zenapptic websites. We may use both session cookies and persistent
              cookies to identify that you have logged in to the Services and to tell us how and
              when you interact with our Services. We may also use cookies to monitor aggregate
              usage and web traffic routing on our Services in order to customize and improve our
              Services. Unlike persistent cookies, session cookies are deleted when you log off from
              the Services and close your browser. Although most browsers automatically accept
              cookies, you can change your browser options to stop automatically accepting cookies
              or to prompt you before accepting cookies. Please note that if you do not accept
              cookies, you may not be able to access all portions or features of the Services. Some
              third-party service providers that we engage (which may include third-party
              advertisers) may also place their own cookies on your browser. Note that this Privacy
              Policy covers only our use of cookies and does not include use of cookies by such
              third parties.
            </li>

            <li>
              <b>Interest-Based Advertising:</b> We may use third-party advertising companies that
              use tracking technologies to serve our ads across the internet. These companies may
              collect information about your visits to the Zenapptic websites (and other websites)
              and your interaction with our ads and other communications. These advertising
              companies serve ads on behalf of us and others on non-affiliated sites, and some of
              those ads may be personalized, meaning that they are intended to be relevant to you
              based on information collected about your visits to the Zenapptic websites and
              elsewhere over time. You have the choice to tell us not to collect and use this
              information, and in some jurisdictions, we will only engage in interest-based
              advertising if you opt-in.
              <b
                >If you would like more information about this practice and to know your choices
                concerning interest-based ads, visit the Networking Advertising Initiative or the
                Digital Advertising Alliance. In Canada please visit:
                <a href="http://youradchoices.ca/choices/" target="_blank"
                  >http://youradchoices.ca/choices/</a
                >, and in the EU:
                <a href="http://www.youronlinechoices.eu/" target="_blank"
                  >http://www.youronlinechoices.eu/</a
                ></b
              >.
            </li>
          </ul>
        </section>

        <section>
          <h2>How do we use your information?</h2>

          <p>We use your information for the purposes described below:</p>

          <ul>
            <li>
              <b>Providing and securing our Services</b>
              <ul>
                <li>
                  We need to identify and authenticate users to ensure, for example, that only those
                  authorized users are able to use the Services.
                </li>
                <li>
                  We use information that you provide when signing up to set up your account,
                  process payments (if applicable), contact you regarding the Services, and manage
                  your account.
                </li>
                <li>
                  We use your contact information and information related to your request to respond
                  to your inquiries, manage our contract with you, respond to your questions and
                  requests, and send you updates and information about the Services.
                </li>
                <li>
                  We use logging and other data such as general location information—for example,
                  the IP address of your browser or device, to help us manage the performance,
                  security and compliance of the Services. Where you have chosen to share your
                  specific location information, we use this information to provide location-based
                  features.
                </li>
                <li>
                  We analyze usage information, your feedback, support queries, and survey responses
                  to help us understand and make improvements to our Services.
                </li>
              </ul>
            </li>

            <li>
              <b>Communicating with you</b>
              <ul>
                <li>
                  We use your contact information where appropriate to send you information about
                  our Services, events, marketing communications consistent with your preferences,
                  and job opportunities. We also use email statistics, such as open rates, to assess
                  the effectiveness of, and to make improvements to our communications.
                </li>
              </ul>
            </li>

            <li>
              <b>Improving our websites and applications</b>
              <ul>
                <li>
                  We use information about you to help us understand usage patterns and other
                  activities on our websites and applications so that we can diagnose problems and
                  make improvements, including enhancing usability and security.
                </li>
              </ul>
            </li>
          </ul>
        </section>

        <section>
          <h2>What are your rights regarding the information about you?</h2>

          <h3>Services information</h3>
          <p>
            When using our Services, you may access, update, or correct most of your Account
            information by logging in to your account to edit your profile.
          </p>

          <p>
            If you have requests that cannot be carried out by logging in to your account, such as
            accessing additional information or deleting information about you, please email our
            privacy team at legal@zenapptic.ai. Please note that we may need to retain certain
            information about you for as long as you maintain an account for our Services, to
            provide you with our Services, for record keeping purposes, for payment processing, to
            comply with our legal and regulatory obligations, to resolve disputes, or to enforce the
            applicable terms of service or other agreement in place between you (or your
            organization) and Zenapptic (the “Terms of Service”).
          </p>

          <p>
            Requests to access, correct, or delete your information will be handled within thirty
            (30) days unless they are unusually extensive or complex, in which case we will advise
            you of the expected timeline to complete the request.
          </p>

          <p>
            If the General Data Protection Regulation (GDPR) applies to our processing of your
            personally identifiable information (which will be the case if you reside in a country
            or are a citizen of a country that is part of the European Union or European Economic
            Area), you have the right to request limited processing, to object to processing, or to
            request data portability.
          </p>

          <h3>Marketing emails, advertising and website browsing</h3>
          <p>
            For marketing communications, you may opt out of Zenapptic marketing communications by
            clicking on the Unsubscribe link in the marketing email you receive.
          </p>

          <h2>Who has access to your information?</h2>
          <p>
            Zenapptic does not rent or sell your information. We restrict access to your information
            to authorized employees and we do not share your information with third parties except
            in the circumstances explained below.
          </p>

          <h3>Employees and Authorized Contractors</h3>
          <p>
            Our employees and authorized contractors may need to access information about you when
            they require this to perform their job. For example, a customer support representative
            would need access to your account to validate your identity and respond to your question
            or request; our email communications team would need access to your contact information
            to send messages correctly and any unsubscribe requests are properly managed; and our
            security staff would need to review information to investigate attempted denial of
            service attacks, fraudulent account activity, or other attempts to compromise the
            Services.
          </p>

          <p>
            All our employees and contractors are required to agree to maintain the confidentiality
            and protect the privacy of your information.
          </p>

          <h3>Service Providers, Authorized Resellers, and Partners</h3>
          <p>
            We will share limited information about you to authorized service providers we use for
            marketing services, communicating with you, managing our customer database, the
            provision of professional services, and providing and managing the Services (including
            hosting data centers, securing our Services, and payment processing).
          </p>

          <p>
            We limit the number of service providers who are permitted to process your Content for
            the purpose of assisting us in delivering the Services. We refer to these service
            providers as “sub-processors” and they are listed on this page.
          </p>

          <p>
            Where you have purchased a service from an authorized reseller or partner, we may
            provide information about you to (and may receive information about you from) the
            reseller or partner as necessary to support your use of the service you purchased.
          </p>

          <p>
            When sharing your information with any of these service providers, resellers and
            partners, we ensure they agree to obligations consistent with this Privacy Policy and
            any other appropriate confidentiality and security measures, and only use your
            information to carry out the Services and your requests.
          </p>

          <h3>Social Networks and Third-Party Services</h3>
          <p>
            Where you are using our Services and have chosen to connect your Social Networks to the
            Services, or if you authorize a Third-Party Service to access your account, you are
            agreeing to provide information about you to the Social Networks and the Third-Party
            Services under their respective terms and privacy policies.
          </p>

          <h3>Successor Entities</h3>
          <p>
            If we are acquired by or merge with another entity (in which case we will require such
            entity to assume our obligations under this privacy policy), if we are involved in a
            bankruptcy, or if the ownership or control of all or part of our Services or their
            assets changes, information about you will be transferred to such entities.
          </p>

          <h3>Law Enforcement, Government Agencies, and Professional Advisors</h3>
          <p>
            We may need to disclose information about you where we believe that it is reasonably
            necessary to comply with a law or regulation, or if we are otherwise legally required to
            do so, such as in response to a court order or legal process, or to establish, protect,
            or exercise our legal rights or to defend against legal claims or demands.
          </p>

          <p>
            In addition, we may be required to disclose information about you if we believe it is
            necessary to investigate, prevent, or take action: (a) against illegal activities,
            fraud, situations involving potential threats to our rights or property (or to the
            rights or property of those who use our Services), or to protect the personal safety of
            any person; or (b) regarding situations that involve the security of our Services, abuse
            of the Services infrastructure, or the Internet in general (such as voluminous spamming
            or denial of service attacks).
          </p>

          <h3>What international data transfers occur at Zenapptic?</h3>
          <p>
            Under the General Data Protection Regulation (GDPR) and other data protection laws,
            information about you may only be transferred from your region to other regions if
            certain requirements are met. Our Services are mainly managed by Zenapptic’s
            headquarters in Canada. As the European Commission considers Canada to be a country
            which provides adequate data protection, information about you may be transferred from
            the EEA to Canada.
          </p>

          <p>
            Zenapptic also uses third-party service providers, such as managed hosting providers,
            credit card processors, and technology partners to provide the software, networking,
            infrastructure and other services required to operate the Services. These third-party
            providers may process or store personal data on servers outside of the EEA and
            Switzerland, including in Canada or the US. We rely on adequacy (if sent to Canada), the
            service provider’s registration in the EU-US Privacy Shield and Swiss-US Privacy Shield
            (if sent to the US), and/or standard contractual clauses (if sent to the US or onward to
            other countries) to ensure that information about you is lawfully transferred under EU
            law.
          </p>

          <p>
            By its nature, social media data can be shared with people around the globe. The Social
            Networks and Third-Party Services that you choose to integrate with our Services may
            collect, store, and process your information from various locations around the world
            according to their own terms and privacy policies. We recommend you review those privacy
            policies carefully.
          </p>

          <h3>How do we safeguard your information?</h3>
          <p>
            Zenapptic maintains industry standard security safeguards to protect your information.
            Access controls are in place to limit access to your information to those who need it to
            perform their jobs. Individuals who are permitted to handle your information must adhere
            to confidentiality obligations. We encrypt data where appropriate to ensure that your
            information is kept private.
          </p>

          <h3>How long do we retain your information?</h3>
          <p>
            In general Zenapptic only stores Content when it is required in providing the Services,
            and we retain your information only as long as required to provide the Services
            requested by you, for record keeping purposes, to comply with our legal obligations,
            resolve disputes, and enforce the terms for the Services. We may store other Content
            that you produce (such as templates) so that you can easily access them on the Services.
          </p>

          <p>
            Aggregated data is used by Zenapptic for analysis, product improvement, and
            troubleshooting purposes.
          </p>

          <p>
            After it is no longer necessary for us to retain information about you, we will dispose
            of it in a secure manner or anonymize the information.
          </p>

          <h2>Zenapptic’s roles under the GDPR and UK data protection laws</h2>
          <p>
            Depending on the situation and the type of data involved, Zenapptic may act as a data
            controller or a data processor.
          </p>

          <h3>Zenapptic as a data controller</h3>
          <p>Zenapptic acts as a data controller when we are:</p>

          <ul>
            <li>
              Collecting information from you to set up and administer your Zenapptic account;
            </li>
            <li>Monitoring usage information on our website;</li>
            <li>
              Managing your contact and other related information to send marketing, Services, and
              other communications to you;
            </li>
            <li>Responding to a support or general inquiry; and</li>
            <li>Recruiting individuals for job opportunities.</li>
          </ul>

          <h3>Legal basis for processing when Zenapptic is a data controller</h3>
          <p>The legal bases for processing information about you include:</p>

          <ul>
            <li>
              Your consent (for example, when you have provided your information to sign up for an
              account).
            </li>
            <li>
              Where we rely on your consent to process personal data, you have the right to withdraw
              your consent at any time;
            </li>
            <li>It is necessary to perform a contract;</li>
            <li>Legitimate interest;</li>
            <li>
              In some cases, we may have a legal obligation to process your personal data to comply
              with relevant laws (for example, processing payroll and tax information to comply with
              relevant employment and tax legislation); or processing is necessary to protect your
              vital interests or those of another person.
            </li>
          </ul>

          <h3>Your rights when Zenapptic is a data controller</h3>
          <p>
            Where Zenapptic is acting as a data controller, we have outlined certain rights in the
            section “What are your rights regarding the information about you?”.
          </p>

          <p>In addition, you may have the following rights:</p>

          <ul>
            <li>
              Right to object to processing: you may request that Zenapptic stops processing
              information about you (for example, to stop sending you marketing communications).
            </li>
            <li>
              Right to restrict processing: you may request that we restrict processing information
              about you (for example, where you believe that this information is inaccurate).
            </li>
            <li>
              Right to data portability: you may request that we provide you with information
              Zenapptic has about you in a structured, machine-readable, and commonly used format,
              and you may request that we transfer this information to another data controller.
            </li>
          </ul>

          <p>
            If you would like assistance on any of the above requests, please email us at
            legal@zenapptic.ai with details of your request.
          </p>

          <h3>Zenapptic as a data processor</h3>
          <p>
            Where you are using our Services and making decisions about the personal data that is
            being processed in the Services, you are acting as a data controller and Zenapptic is
            acting as a data processor.
          </p>

          <p>
            There are certain obligations under the GDPR that you have as a data controller,
            including being responsible for managing Content on the Services. As a data processor,
            Zenapptic will only access and process Content to provide you with the Services, the
            Terms of Service, and applicable laws. As part of delivering the Services, we may
            process Content to further improve the Services, such as enhancing usability and
            developing new features.
          </p>

          <h2>California Privacy Rights</h2>
          <p>
            If you are a consumer as defined in the California Consumer Privacy Act (CCPA), the
            following provisions apply to you. Definitions of terms are set out in the CCPA.
          </p>

          <h3>Information about your Personal Information</h3>
          <ul>
            <li>
              The categories and specific types of personal information collected and sources from
              which personal information are collected are outlined in “What information do we
              collect?”;
            </li>
            <li>
              The business and commercial purposes for collecting personal information are outlined
              in “How do we use your information?”;
            </li>
            <li>
              The categories of third parties with whom your information is shared in “Who has
              access to your information?”.
            </li>
          </ul>

          <h3>Your Rights</h3>
          <p>
            We have outlined certain rights in the section “What are your rights regarding the
            information about you?”.
          </p>

          <p>Under the CCPA, you may have the following specific rights:</p>

          <ul>
            <li>
              The right to know about the personal information collected about you, which we have
              set out under “Information about your Personal Information”;
            </li>
            <li>The right to have your personal information deleted.</li>
            <li>
              The right not to be discriminated against for exercising consumer rights under the
              CCPA.
            </li>
          </ul>

          <p>
            You may exercise your rights or request a CCPA addendum by emailing us at
            legal@zenapptic.ai.
          </p>

          <p>
            While we disclose personal information to service providers for the purpose of managing
            our relationship with you (e.g. distributing marketing communications) and providing the
            Services, we do not sell your personal information.
          </p>
        </section>

        <section>
          <h2>Changes to this Privacy Policy</h2>
          <p>
            We may make changes to this privacy policy at any time to reflect updates to our
            Services, applicable laws, and other factors. We will include a notice on the website
            and/or our Services, but we encourage you to stay informed by reviewing this policy
            periodically.
          </p>
        </section>

        <section>
          <h2>How to contact us</h2>
          <p>
            If you have any questions, concerns or feedback, please email us at legal@zenapptic.ai.
          </p>
        </section>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.privacy-page {
  color: white;
  margin: auto;
  max-width: 1024px;
  padding: 80px 2rem;
  h1 {
    font-size: 4.2rem;
    font-weight: 400;
    text-align: center;
    text-transform: uppercase;
  }

  .body {
    h2 {
      font-size: 2rem;
    }
    p,
    ul,
    h3 {
      font-size: 1.5rem;

      li {
        padding-bottom: 1rem;
      }
    }

    p {
      margin-top: 0;
      padding-top: 0;
    }

    h3 {
      margin: 0;
      padding: 0;
    }

    a {
      color: #f0f0e6;
    }
  }
}
</style>
